<template>
  <div class="hidden md:block notebook-sidebar h-full min-h-0">
    <div class="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between pb-3 px-1 shrink-0">
        <h3 class="font-bold text-[14px] text-gray-800 dark:text-gray-100 flex items-center gap-1.5">
          {{ t('notebookSidebar.pages') }} <span class="text-gray-400 font-medium">&middot; {{ props.pages.length }}</span>
        </h3>
      </div>

      <div ref="chipsRef" class="flex-1 min-h-0 flex flex-col gap-2 overflow-y-auto pr-1 pb-2">
        <button v-for="(p, idx) in props.pages" :key="p.uid" type="button"
          :ref="el => { if (el) menuRefs[idx] = el }"
          class="group relative grid grid-cols-[24px_minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 px-3 py-3 rounded-md border text-xs w-full transition-colors items-center"
          :class="idx === props.page
            ? 'bg-indigo-50/50 border-indigo-200 dark:bg-indigo-900/30 dark:border-indigo-800'
            : 'bg-white border-gray-100 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700'"
          @click="$emit('select', idx)"
        >
          <!-- Row 1: Number -->
          <span class="flex text-[11px] items-center justify-center rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300 w-6 h-6"
                :class="idx === props.page ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-800 dark:text-indigo-200' : ''">
            {{ idx + 1 }}
          </span>

          <!-- Row 1: Title -->
          <span class="font-medium text-[14px] truncate text-left"
                :class="idx === props.page ? 'text-indigo-800 dark:text-indigo-200' : 'text-gray-700 dark:text-gray-200'">
            {{ props.titles[idx] || `${t('notebookSidebar.page')} ${idx + 1}` }}{{ p.isModified ? ' *' : '' }}
          </span>

          <!-- Row 1: Menu -->
          <span class="flex justify-end">
            <EllipsisVerticalIcon class="w-5 h-5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 cursor-pointer" @click="(e) => toggleMenu(idx, e)" />
          </span>

          <!-- Row 2: Drag Handle -->
          <span class="flex justify-center text-gray-300 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 cursor-grab" data-drag-handle="true" @click.stop>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="5" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>
          </span>

          <!-- Row 2: Artist -->
          <span class="text-[12px] text-gray-400 dark:text-gray-500 truncate text-left">
            {{ p.subtitle || p.artist?.name || props.previews[idx] || '' }}
          </span>

          <!-- Row 2: Tag -->
          <span class="flex justify-end">
            <span v-if="p.tags && p.tags.length > 0" 
                  class="px-2.5 py-0.5 rounded-md text-[10px] font-bold whitespace-nowrap"
                  :style="getTagStyle(p.tags[0])">
              {{ p.tags[0].name || p.tags[0] }}
            </span>
          </span>
        </button>

        <Teleport to="body">
          <div v-if="openMenuIndex !== -1" 
               ref="dropdownMenuRef"
               class="fixed w-48 bg-white dark:bg-gray-700 rounded-md shadow-lg z-[9999] border border-gray-200 dark:border-gray-600 flex flex-col text-left text-sm py-1"
               :style="{ top: menuPosition.top + 'px', left: menuPosition.left + 'px' }"
               @click.stop>
            <div class="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-200" @click="handleAddSongToFavorite(openMenuIndex)">{{ t('notebookSidebar.addSongToFavorite') }}</div>
            <div class="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-200" @click="handleMoveToTop(openMenuIndex)">{{ t('notebookSidebar.moveToTop') }}</div>
            <div class="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-200" @click="handleMoveToBottom(openMenuIndex)">{{ t('notebookSidebar.moveToBottom') }}</div>
            <div class="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-200" @click="handleCreateVersion(openMenuIndex)">{{ t('notebookSidebar.createVersion') }}</div>
            <div v-if="props.pages[openMenuIndex]?._id" class="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-200" @click="handleShowVersions(openMenuIndex)">{{ t('notebookSidebar.selectVersion') }}</div>
          </div>
        </Teleport>
      </div>

      <!-- Footer Button -->
      <div class="pt-2 flex items-center w-full gap-2 shrink-0 mt-1">
        <button class="flex-1 py-2.5 rounded-xl bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 font-bold text-xs hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors" @click="$emit('addPage')">
          + {{ t('notebookSidebar.addPage') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, nextTick, onMounted, defineEmits, onBeforeUnmount } from 'vue'
import { EllipsisVerticalIcon } from '@heroicons/vue/24/outline'
import Sortable from 'sortablejs'
import { useI18n } from 'vue-i18n'
import { generateTagColors, getLocalizedTagName } from '@/service/tagColor.js'

const { t, locale } = useI18n()

const getTagName = (tag) => {
  return getLocalizedTagName(tag, locale.value);
};

const getTagStyle = (tag) => {
  const name = getTagName(tag);
  if (typeof tag === 'object' && tag !== null) {
      const bg = tag.color_background;
      const fg = tag.color_font;
      if (bg && fg) {
          return { backgroundColor: bg, color: fg };
      }
  }
  if (name) {
      const c = generateTagColors(name);
      return { backgroundColor: c.color_background, color: c.color_font };
  }
  return { backgroundColor: '#f3f4f6', color: '#374151' };
};

const props = defineProps({
  pages: { type: Array, default: () => [] },
  page: { type: Number, default: 0 },
  titles: { type: Array, default: () => [] },
  previews: { type: Array, default: () => [] }
})

const emit = defineEmits(['select', 'reorder', 'moveToTop', 'moveToBottom', 'createVersion', 'showVersions', 'addSongToFavorite', 'addPage'])

const chipsRef = ref(null)
const dropdownMenuRef = ref(null)
const openMenuIndex = ref(-1)
const menuRefs = ref([])
const menuPosition = ref({ top: 0, left: 0 })

const toggleMenu = (idx, event) => {
  event.stopPropagation()
  if (openMenuIndex.value === idx) {
    openMenuIndex.value = -1
    return
  }
  openMenuIndex.value = idx
  // Compute position from the ellipsis icon that was clicked
  const rect = event.currentTarget.getBoundingClientRect()
  const menuWidth = 192 // w-48
  const itemCount = props.pages[idx]?._id ? 5 : 4
  const estimatedHeight = itemCount * 36 + 10
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth

  let top = rect.bottom + 4
  if (top + estimatedHeight > viewportHeight - 8 && rect.top - estimatedHeight - 4 >= 8) {
    top = rect.top - estimatedHeight - 4
  }

  let left = rect.right - menuWidth
  if (left < 8) left = 8
  if (left + menuWidth > viewportWidth - 8) left = viewportWidth - menuWidth - 8

  menuPosition.value = { top, left }

  nextTick(() => {
    if (dropdownMenuRef.value) {
      const menuRect = dropdownMenuRef.value.getBoundingClientRect()
      let adjustedTop = menuPosition.value.top
      if (rect.bottom + 4 + menuRect.height > viewportHeight - 8 && rect.top - menuRect.height - 4 >= 8) {
        adjustedTop = rect.top - menuRect.height - 4
      } else if (adjustedTop + menuRect.height > viewportHeight - 8) {
        adjustedTop = Math.max(8, viewportHeight - menuRect.height - 8)
      }
      menuPosition.value.top = adjustedTop
    }
  })
}

const closeMenu = () => {
  openMenuIndex.value = -1
}

onMounted(() => {
  document.addEventListener('click', closeMenu)
  if (chipsRef.value) {
    Sortable.create(chipsRef.value, {
      animation: 150,
      handle: '[data-drag-handle]',
      onEnd: (evt) => emit('reorder', { oldIndex: evt.oldIndex, newIndex: evt.newIndex })
    })
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeMenu)
})

const handleAddSongToFavorite = (idx) => {
  emit('addSongToFavorite', idx)
  closeMenu()
}

const handleMoveToTop = (idx) => {
  emit('moveToTop', idx)
  closeMenu()
}

const handleMoveToBottom = (idx) => {
  emit('moveToBottom', idx)
  closeMenu()
}

const handleCreateVersion = (idx) => {
  emit('createVersion', idx)
  closeMenu()
}

const handleShowVersions = (idx) => {
  console.log('[NotebookSidebar] handleShowVersions called for idx:', idx, 'page:', props.pages[idx])
  console.log('[NotebookSidebar] Emitting showVersions event')
  emit('showVersions', idx)
  closeMenu()
}
</script>


